import axios, { AxiosRequestConfig } from 'axios';

import { HOST_API, LOCAL_STORAGE_TOKEN, SELECTED_FRANCHISEE } from 'src/config-global';

const axiosInstance = axios.create({ baseURL: HOST_API });
const axiosNotAuthenticatedInstance = axios.create({ baseURL: HOST_API });

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => {
    const originalRequest = error.config;

    // Se o erro for 401 (não autorizado)
    if (
      error?.response?.status === 401 ||
      (error?.response?.status === 403 && !error.request.responseURL.includes('franchisees'))
    ) {
      const token = localStorage?.getItem(LOCAL_STORAGE_TOKEN);

      // Log detalhado para depuração
      console.warn(
        `Unauthorized: ${error}, LOCAL_STORAGE_AVAILABLE: ${!!localStorage}, LOCAL_STORAGE_TOKEN: ${token}, URL: ${originalRequest.url}, Method: ${originalRequest.method}`
      );

      // Se for uma requisição XML, adiciona informação de erro específica
      if (
        error.request.responseType === 'document' ||
        (error.request.responseHeaders &&
          error.request.responseHeaders['content-type']?.includes('xml'))
      ) {
        console.error('Erro XML detectado na resposta, possível problema de proxy ou WAF');
      }

      // Remove apenas o token em vez de limpar todo o localStorage
      localStorage.removeItem(LOCAL_STORAGE_TOKEN);

      // Recarrega a página apenas se o usuário estiver em uma área protegida
      if (!window.location.pathname.includes('/login')) {
        // Redireciona com um pequeno atraso para permitir que os logs sejam enviados
        setTimeout(() => {
          window.location.href = '/login';
        }, 300);
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

axiosNotAuthenticatedInstance.interceptors.response.use(
  (res) => res,
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(LOCAL_STORAGE_TOKEN);
    const selectedFranchisee = localStorage.getItem(SELECTED_FRANCHISEE);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (selectedFranchisee) {
      config.headers.Franchisee = selectedFranchisee;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const axiosInstances = {
  authenticated: axiosInstance,
  notAuthenticated: axiosNotAuthenticatedInstance,
};

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

export const endpoints = {
  auth: {
    sendMagicLink: 'api/v1/backoffice-bff/auth/magic-link/request',
    verifyMagicLinkSecret: 'api/v1/backoffice-bff/auth/magic-link/login',
  },
  calendar: '/api/calendar',
  billing: {
    list: 'api/v1/backoffice-bff/billings',
    details: (billingId: string) => `/api/v1/backoffice-bff/billings?billingId=${billingId}`,
    types: (franchiserId: string, franchiseeId: string) =>
      `/api/v1/backoffice-bff/config/franchiser/${franchiserId}/franchisee/${franchiseeId}/billing-types`,
  },
  franchisees: {
    list: '/api/v1/backoffice-bff/franchisees',
    active: '/api/v1/backoffice-bff/franchisees/active',
    contacts: {
      list: (franchiseeId: string) => `/api/v1/backoffice-bff/franchisees/${franchiseeId}/contacts`,
      create: (franchiseeId: string) =>
        `/api/v1/backoffice-bff/franchisees/${franchiseeId}/contacts`,
    },
  },
  menloShops: {
    order: {
      list: 'api/v1/backoffice-bff/shopping/order',
      getById: (id: string) => `api/v1/backoffice-bff/shopping/order/${id}`,
      patchStatus: (id: string) => `api/v1/backoffice-bff/shopping/order/${id}/status`,
      patchPaymentStatus: (id: string) =>
        `api/v1/backoffice-bff/shopping/order/${id}/payment_status`,
      createOrder: `api/v1/backoffice-bff/shopping/order`,
      grouped: 'api/v1/backoffice-bff/shopping/order/grouped',
      suppliers: (groupId: string) => `api/v1/backoffice-bff/shopping/order?GroupId=${groupId}`,
      markOrderAsDelivered: 'api/v1/backoffice-bff/shopping/order/mark-order-as-delivered',
    },
    products: {
      list: 'api/v1/backoffice-bff/shopping/product',
      getById: (id: string) => `api/v1/backoffice-bff/shopping/product/${id}`,
      grouped: 'api/v1/backoffice-bff/shopping/product/checkout/payment-options',
    },
    productsCampaign: {
      list: 'api/v1/backoffice-bff/shopping/promotion',
    },
    pending: {
      list: `api/v1/backoffice-bff/shopping/pending`,
      getMissingById: (pendingId: string) =>
        `api/v1/backoffice-bff/shopping/pending/missing/${pendingId}`,
      getApprovalById: (pendingId: string) =>
        `api/v1/backoffice-bff/shopping/pending/approval/${pendingId}`,
      patchStatus: (pendingId: string) => `api/v1/backoffice-bff/shopping/pending/${pendingId}`,
      updateMissing: 'api/v1/backoffice-bff/shopping/pending/missing',
    },
    suppliers: {
      list: 'api/v1/backoffice-bff/shopping/supplier',
    },
  },
  checkout: {
    getOrderByPublicId: (orderPublicId: string) =>
      `api/v1/backoffice-bff/singer/checkout/${orderPublicId}`,
    tokenize: (orderPublicId: string) =>
      `api/v1/backoffice-bff/singer/checkout/${orderPublicId}/card`,
    creditCardPayment: (orderPublicId: string) =>
      `api/v1/backoffice-bff/singer/checkout/${orderPublicId}/payment`,
    getPaymentOption: (orderPublicId: string, method: string) =>
      `api/v1/backoffice-bff/singer/checkout/${orderPublicId}/payment-options/${method}`,
  },
};
